import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import FeatureDetailsModal from "./FeatureDetailsModal";
import FeatureCard, { FeatureItem, FeatureStatus } from "./FeaturesCard";
import UpcomingFeaturesMinimisedPill from "./UpcomingFeaturesMinimized";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Component {
  id: number;
  name: string;
}

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  profile_picture?: string | null;
}

interface UpcomingFeature {
  id: number;
  component: Component;
  lead_developer: User;
  staging_date?: string | null;
  live_date?: string | null;
  status: "in-progress" | "complete" | "overdue";
}

type MobileTab = "summary" | FeatureStatus;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getUserLabel = (u: User) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username;

const resolveStatus = (f: UpcomingFeature): FeatureStatus => {
  if (f.status === "complete") return "complete";
  if (f.live_date) {
    const live = new Date(f.live_date);
    live.setHours(23, 59, 59, 999);
    if (live < new Date()) return "overdue";
  }
  return f.status === "overdue" ? "in-progress" : f.status;
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const toFeatureItem = (f: UpcomingFeature): FeatureItem => ({
  name: f.component.name,
  lead: getUserLabel(f.lead_developer),
  stagingDate: formatDate(f.staging_date),
  liveDate: formatDate(f.live_date),
  status: resolveStatus(f),
  leadPicture: f.lead_developer.profile_picture ?? null,
});

const fetchPaginated = async <T,>(url: string): Promise<T[]> => {
  let all: T[] = [];
  let next: string | null = url;
  while (next) {
    const res: { data: { results?: T[]; next?: string | null } | T[] } =
      await apiService.get(next);
    if (Array.isArray(res.data)) {
      all = res.data;
      break;
    }
    const paged = res.data as { results?: T[]; next?: string | null };
    all = [...all, ...(paged.results ?? [])];
    if (paged.next) {
      const u = new URL(paged.next);
      next = u.pathname + u.search;
    } else {
      next = null;
    }
  }
  return all;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const LEAD_COLORS: Record<string, string> = {
  Ruva: "#a89cf7",
  Nigel: "#9fe7c8",
  Leon: "#f7b99c",
};

const bounceKeyframe = {
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-2px)" },
  },
};

const MOBILE_TABS: { key: MobileTab; label: string; color: string }[] = [
  { key: "summary", label: "Summary", color: "#94a3b8" },
  { key: "overdue", label: "Overdue", color: "#E11D48" },
  { key: "in-progress", label: "In Progress", color: "#f59e0b" },
  { key: "complete", label: "Complete", color: "#10b981" },
];

interface UpcomingFeaturesProps {
  expanded: boolean;
  onMinimise: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onCountsChange?: (counts: {
    inProgress: number;
    complete: number;
    overdue: number;
  }) => void;
}

export const fetchFeatureCounts = async () => {
  const raw = await fetchPaginated<UpcomingFeature>(
    "/api/configurations/upcoming-features/",
  );
  const mapped = raw.map(toFeatureItem);
  return {
    inProgress: mapped.filter((f) => f.status === "in-progress").length,
    complete: mapped.filter((f) => f.status === "complete").length,
    overdue: mapped.filter((f) => f.status === "overdue").length,
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const UpcomingFeatures = ({
  expanded,
  onMinimise,
  onPrev,
  onCountsChange,
  onNext,
}: UpcomingFeaturesProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);

  const [mobileTab, setMobileTab] = useState<MobileTab>("summary");
  const [selectedFeature, setSelectedFeature] = useState<FeatureItem | null>(null);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [loading, setLoading] = useState(false);

  const overdueCount = features.filter((f) => f.status === "overdue").length;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const raw = await fetchPaginated<UpcomingFeature>(
          "/api/configurations/upcoming-features/",
        );
        const mapped = raw.map(toFeatureItem);
        setFeatures(mapped);
        onCountsChange?.({
          inProgress: mapped.filter((f) => f.status === "in-progress").length,
          complete: mapped.filter((f) => f.status === "complete").length,
          overdue: mapped.filter((f) => f.status === "overdue").length,
        });
      } catch {
        // fail silently
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [onCountsChange]);

  

  const sorted = [...features].sort((a, b) => {
    if (!a.liveDate) return 1;
    if (!b.liveDate) return -1;
    return new Date(a.liveDate).getTime() - new Date(b.liveDate).getTime();
  });

  const inProgressCount = features.filter((f) => f.status === "in-progress").length;
  const completeCount = features.filter((f) => f.status === "complete").length;
  const leads = Array.from(new Set(features.map((f) => f.lead)));

  if (!expanded) {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-end", alignSelf: "stretch" }}>
        <UpcomingFeaturesMinimisedPill
          inProgressCount={inProgressCount}
          completeCount={completeCount}
          onExpand={onMinimise}
          overdueCount={overdueCount}
        />
      </Box>
    );
  }

  const statusColumns: { status: FeatureStatus; label: string; color: string }[] = [
    { status: "overdue", label: "OVER DUE", color: "#E11D48" },
    { status: "in-progress", label: "IN PROGRESS", color: "#f59e0b" },
    { status: "complete", label: "COMPLETE", color: "#10b981" },
  ];

  // ─── Shared top nav ───────────────────────────────────────────────────────
  const TopNav = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <IconButton
        onClick={() => onPrev?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
      </IconButton>

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9rem",
          color: colors.primary[100],
          minWidth: { xs: 140, sm: 180 },
          textAlign: "center",
        }}
      >
        Upcoming Features
      </Typography>

      <IconButton
        onClick={() => onNext?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    const activeTabConfig = MOBILE_TABS.find((t) => t.key === mobileTab)!;
    const tabCountMap: Record<string, number> = {
      summary: features.length,
      overdue: features.filter((f) => f.status === "overdue").length,
      "in-progress": inProgressCount,
      complete: completeCount,
    };
    const mobileItems = mobileTab !== "summary"
      ? sorted.filter((f) => f.status === (mobileTab as FeatureStatus))
      : [];

    return (
      <Box sx={{ width: "100%", px: 1 }}>
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "24px",
            p: 2,
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          {/* Minimise */}
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 12,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          {/* Tab strip */}
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mb: 2,
              backgroundColor: colors.primary[800],
              borderRadius: "14px",
              p: 0.5,
            }}
          >
            {MOBILE_TABS.map((tab) => {
              const isActive = mobileTab === tab.key;
              const count = tabCountMap[tab.key] ?? 0;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setMobileTab(tab.key)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.3,
                    py: 1,
                    px: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: isActive ? colors.primary[900] : "transparent",
                    transition: "background-color 0.2s ease",
                    borderBottom: isActive ? `2px solid ${tab.color}` : "2px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? colors.primary[400] : colors.primary[700],
                      lineHeight: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </Typography>
                  {tab.key !== "summary" && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: isActive ? tab.color : colors.primary[700],
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Tab content */}
          {mobileTab === "summary" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Status counts */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 0.5,
                  }}
                >
                  STATUS OVERVIEW
                </Typography>
                {[
                  { label: "Over Due", count: overdueCount, color: "#E11D48", tab: "overdue" as MobileTab },
                  { label: "In Progress", count: inProgressCount, color: "#f59e0b", tab: "in-progress" as MobileTab },
                  { label: "Complete", count: completeCount, color: "#10b981", tab: "complete" as MobileTab },
                ].map(({ label, count, color, tab }) => (
                  <Box
                    key={label}
                    onClick={() => setMobileTab(tab)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      py: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: "0.85rem", color: colors.primary[100] }}>
                        {label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={count}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: color + "20",
                          color: color,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                      <ChevronRight sx={{ fontSize: 14, color: colors.primary[700] }} />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Feature leads */}
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  FEATURE LEADS
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {leads.map((lead) => {
                    const count = features.filter((f) => f.lead === lead).length;
                    const color = LEAD_COLORS[lead] ?? "#9fe7f2";
                    const pic = features.find((f) => f.lead === lead)?.leadPicture;
                    return (
                      <Tooltip key={lead} title={`${lead}: ${count} features`} arrow>
                        <Box sx={{ position: "relative", cursor: "pointer" }}>
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: "50%",
                              border: `2px solid ${color}`,
                              overflow: "hidden",
                              backgroundColor: color + "30",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {pic ? (
                              <Box
                                component="img"
                                src={pic}
                                alt={lead}
                                sx={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color }}>
                                {lead[0]}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -3,
                              right: -3,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${colors.primary[900]}`,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "0.6rem", color: "white", fontWeight: 700, lineHeight: 1 }}
                            >
                              {count}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "60vh",
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                pb: 1,
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={24} sx={{ color: activeTabConfig.color }} />
                </Box>
              ) : mobileItems.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: activeTabConfig.color + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: activeTabConfig.color,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem", color: colors.primary[400] }}>
                    No {activeTabConfig.label.toLowerCase()} features
                  </Typography>
                </Box>
              ) : (
                mobileItems.map((f) => (
                  <Box
                    key={f.name}
                    onClick={() => {
                      setSelectedFeature(f);
                      setFeatureModalOpen(true);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "16px",
                      backgroundColor: colors.primary[800],
                      cursor: "pointer",
                      borderLeft: `3px solid ${activeTabConfig.color}`,
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: colors.primary[700] + "60" },
                    }}
                  >
                    {/* Lead avatar */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        border: `2px solid ${LEAD_COLORS[f.lead] ?? "#9fe7f2"}`,
                        overflow: "hidden",
                        backgroundColor: (LEAD_COLORS[f.lead] ?? "#9fe7f2") + "30",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {f.leadPicture ? (
                        <Box
                          component="img"
                          src={f.leadPicture}
                          alt={f.lead}
                          sx={{ width: 40, height: 40, objectFit: "cover" }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            color: LEAD_COLORS[f.lead] ?? "#9fe7f2",
                          }}
                        >
                          {f.lead[0]}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: colors.primary[100],
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {f.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: colors.primary[700] }}>
                        {f.lead} · {f.liveDate || "No date"}
                      </Typography>
                    </Box>
                    <ChevronRight sx={{ fontSize: 16, color: colors.primary[700], flexShrink: 0 }} />
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        <FeatureDetailsModal
          open={featureModalOpen}
          feature={selectedFeature}
          onClose={() => setFeatureModalOpen(false)}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "fit-content",
        maxWidth: { xs: "calc(100vw - 32px)", md: "fit-content" },
      }}
    >
      <Box
        sx={{
          border: `1px solid ${colors.primary[300]}30`,
          borderRadius: "40px",
          p: { xs: 1.5, md: 3 },
          boxShadow: "0 6px 28px rgba(7, 7, 7, 0.06)",
          backgroundColor: colors.primary[900],
          position: "relative",
        }}
      >
        {/* Minimise */}
        <Tooltip title="Minimise" arrow>
          <IconButton
            onClick={onMinimise}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 16,
              zIndex: 3,
              color: colors.primary[700],
              "&:hover": { color: colors.primary[400] },
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {TopNav}

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Summary panel */}
          <Box
            sx={{
              minWidth: { xs: 200, sm: 240, md: 260 },
              maxWidth: { xs: 200, sm: 240, md: 260 },
              backgroundColor: colors.primary[800],
              borderRadius: "30px",
              p: 2.5,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                color: colors.primary[700],
              }}
            >
              UPCOMING FEATURES
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
              {[
                { label: "In Progress", count: inProgressCount, color: "#f0b445" },
                { label: "Complete", count: completeCount, color: "#4ecb8d" },
              ].map(({ label, count, color }) => (
                <Box
                  key={label}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: "0.8rem", color: colors.primary[100] }}>
                      {label}
                    </Typography>
                  </Box>
                  <Chip
                    label={count}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: colors.primary[700] + "30",
                      color: colors.primary[100],
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* Feature leads */}
            <Box
              sx={{
                borderRadius: "20px",
                backgroundColor: colors.primary[900],
                px: 1.5,
                py: 2,
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: colors.primary[700], mb: 1.5 }}>
                Feature leads
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {leads.map((lead) => {
                  const count = features.filter((f) => f.lead === lead).length;
                  const color = LEAD_COLORS[lead] ?? "#9fe7f2";
                  return (
                    <Tooltip key={lead} title={`${lead}: ${count} features`} arrow>
                      <Box sx={{ position: "relative", cursor: "pointer" }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            border: `2px solid ${color}`,
                            overflow: "hidden",
                            backgroundColor: color + "30",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {features.find((f) => f.lead === lead)?.leadPicture ? (
                            <Box
                              component="img"
                              src={features.find((f) => f.lead === lead)!.leadPicture!}
                              alt={lead}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                objectFit: "cover",
                                objectPosition: "center",
                                display: "block",
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color }}>
                              {lead[0]}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -4,
                            right: -4,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1.5px solid ${colors.primary[900]}`,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "0.6rem", color: "white", fontWeight: 700, lineHeight: 1 }}
                          >
                            {count}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Status columns */}
          {statusColumns.map(({ status, label, color }) => {
            const items = sorted.filter((f) => f.status === status);
            return (
              <Box
                key={status}
                sx={{
                  minWidth: { xs: 200, sm: 240, md: 260 },
                  maxWidth: { xs: 200, sm: 240, md: 260 },
                  borderRadius: "30px",
                  p: 2,
                  backgroundColor: colors.primary[800],
                  flexShrink: 0,
                  position: "relative",
                  borderTop: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderLeft: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderRight: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderBottom: loading ? `4px solid transparent` : `4px solid ${color}60`,
                  backgroundImage: loading
                    ? `linear-gradient(${colors.primary[800]}, ${colors.primary[800]}), linear-gradient(90deg, ${color}15, ${color}, ${color}15)`
                    : "none",
                  backgroundOrigin: loading ? "border-box" : "unset",
                  backgroundClip: loading ? "padding-box, border-box" : "unset",
                  backgroundSize: loading ? "200% auto" : "unset",
                  animation: loading ? "shimmer 1.5s linear infinite" : "none",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "-200% center" },
                    "100%": { backgroundPosition: "200% center" },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.08em",
                    color: color,
                    mb: 1.5,
                  }}
                >
                  {label}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: 3 * 72 + 2 * 8,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                      <CircularProgress size={20} sx={{ color: color }} />
                    </Box>
                  ) : items.length === 0 ? (
                    <Typography
                      sx={{ fontSize: "0.75rem", color: colors.primary[700], textAlign: "center", mt: 2 }}
                    >
                      No entries
                    </Typography>
                  ) : (
                    items.map((f) => (
                      <FeatureCard
                        key={f.name}
                        feature={f}
                        colors={colors}
                        onClick={() => {
                          setSelectedFeature(f);
                          setFeatureModalOpen(true);
                        }}
                      />
                    ))
                  )}
                </Box>

                {!loading && items.length > 3 && (
                  <Box
                    sx={{
                      ...bounceKeyframe,
                      position: "absolute",
                      bottom: 4,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 2,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      animation: "bounce 1.2s ease-in-out infinite",
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 18, color: colors.primary[400], mt: "-6px" }} />
                    <KeyboardArrowDown
                      sx={{ fontSize: 18, color: colors.primary[400], mt: "-10px", opacity: 0.5 }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <FeatureDetailsModal
        open={featureModalOpen}
        feature={selectedFeature}
        onClose={() => setFeatureModalOpen(false)}
      />
    </Box>
  );
};

export default UpcomingFeatures;